import { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_USERDATA_QUERY } from '../services/GraphQLQueries'
import SideMenu from '../layouts/SideMenu'
import '../layouts/stylesheets/index.css'
import '../layouts/stylesheets/loader-style.css'
import { useNavigate } from 'react-router-dom'

const SALES_EMAIL = 'support@metrinome.io'

const pricingTiers = [
  {
    id: 'core',
    name: 'Core',
    annualValue: '$30,000 / year',
    commitment: 'Up to 20,000 CVEs / year',
    rate: '$1.50 per CVE',
    audience: 'For teams validating conversion integrity.',
    accent: 'bg-[#E6F7DD]',
    features: [
      'Annual commitment to CVE processing capacity',
      'All processed conversion events count toward usage',
      'Independent click-to-conversion verification layer',
      'Verification, classification, storage, and reporting',
    ],
  },
  {
    id: 'scale',
    name: 'Scale',
    annualValue: '$100,000 / year',
    commitment: 'Up to 100,000 CVEs / year',
    rate: '$1.00 per CVE',
    audience: 'For multi-channel programs with sustained volume.',
    accent: 'bg-[#F2F8E8]',
    features: [
      'Higher annual CVE processing capacity',
      'Designed for ongoing multi-channel verification workloads',
      'Capacity-based pricing aligned to processed events',
      'Operational visibility across verified and unverifiable conversions',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    annualValue: '$225,000 / year',
    commitment: 'Up to 300,000 CVEs / year',
    rate: '$0.75 per CVE',
    audience: 'For complex, high-volume programs.',
    accent: 'bg-[#F7F3E8]',
    features: [
      'Large-scale conversion verification capacity',
      'Lower per-event rate at higher annual commitments',
      'Built for complex attribution and verification operations',
      'Supports substantial annual processing volume',
    ],
  },
  {
    id: 'enterprise-plus',
    name: 'Enterprise+',
    annualValue: '$300,000+ / year',
    commitment: '300,000+ CVEs / year',
    rate: '$0.50 to $0.65 per CVE',
    audience: 'For custom enterprise verification infrastructure.',
    accent: 'bg-[#EEF0FF]',
    features: [
      'Greater of annual minimum fee or committed-volume pricing',
      'Custom pricing based on annual committed CVE capacity',
      'Best fit for the largest verification programs',
      'Structured for tailored commercial agreements',
    ],
  },
]

export default function SelectPlan() {
  const navigate = useNavigate()
  const [fullName, setFullName] = useState('')

  useEffect(() => {
    const token = localStorage.getItem('ontrack-auth-token')
    if (!token) {
      navigate('/signin')
    }
  }, [navigate])

  const { data: userData, loading: isLoadingUser } = useQuery(GET_USERDATA_QUERY)

  const isLoading = isLoadingUser

  useEffect(() => {
    if (userData?.userdata?.fullName) {
      setFullName(userData.userdata.fullName)
    }
  }, [userData])

  const handleContactSales = (tier) => {
    const subject = encodeURIComponent(`Metrinome ${tier.name} pricing inquiry`)
    const body = encodeURIComponent(
      `Hi Metrinome team,\n\nI'm interested in the ${tier.name} annual CVE contract.\n\nTier details:\n- Annual value: ${tier.annualValue}\n- Capacity: ${tier.commitment}\n- Rate: ${tier.rate}\n\nPlease contact me about next steps.\n`
    )
    window.location.href = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`
  }

  return (
    <div className="flex min-h-screen w-full items-stretch" aria-live="polite">
      <SideMenu className="sticky top-0 shrink-0" hideBackArrow />
      <div className="w-full flex justify-center">
        {isLoading ? (
          <div className="loader-wrapper">
            <div className="flex justify-center items-center">
              <div className="grid gap-10">
                <div className="loader"></div>
                <div className="text-center">Loading pricing...</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-7xl px-6 py-12">
            <div className="mx-auto max-w-5xl text-center mb-12">
              <p className="text-sm uppercase tracking-[0.35em] text-[#6B7D70]">Metrinome Pricing</p>
              <h1 className="mt-4 text-4xl font-semibold font-unbounded text-gray-900">
                {fullName ? `Welcome, ${fullName}` : 'Conversion verification pricing'}
              </h1>
              <p className="mt-5 text-lg text-gray-700 leading-8">
                Metrinome is priced on annual Conversion Verification Event capacity. Each processed conversion event is one CVE, and pricing scales with verification workload rather than click volume or media spend.
              </p>
              <div className="mt-6 rounded-3xl border border-[#D8E4DA] bg-[#FAFCFA] px-6 py-5 text-left shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6B7D70]">How billing works</p>
                <div className="mt-3 grid gap-3 md:grid-cols-3 text-sm text-gray-700">
                  <p>Clients commit to annual CVE processing capacity, not ad spend.</p>
                  <p>Both verified and unverifiable processed conversions count as CVEs.</p>
                  <p>Annual contract value is tied to committed capacity and per-CVE rate.</p>
                </div>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4 mb-12">
              {pricingTiers.map((tier) => (
                <div
                  key={tier.id}
                  className="flex h-full flex-col rounded-[28px] border border-[#D9E4DC] bg-white p-7 shadow-[0_18px_50px_rgba(27,50,33,0.08)]"
                >
                  <div className={`inline-flex w-fit rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#294235] ${tier.accent}`}>
                    {tier.name}
                  </div>
                  <p className="mt-5 text-3xl font-semibold text-[#1E2A22]">{tier.annualValue}</p>
                  <p className="mt-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#6B7D70]">
                    {tier.commitment}
                  </p>
                  <p className="mt-2 text-lg font-medium text-[#294235]">{tier.rate}</p>
                  <p className="mt-4 text-sm leading-7 text-gray-700">{tier.audience}</p>

                  <ul className="mt-6 flex-1 space-y-3 text-sm text-gray-700">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <span className="mt-2 h-2.5 w-2.5 rounded-full bg-[#294235]"></span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => handleContactSales(tier)}
                    className="mt-8 rounded-2xl bg-[#D8F0CF] px-4 py-3 font-unbounded text-sm text-black transition hover:-translate-y-0.5 hover:brightness-105"
                  >
                    Contact Sales
                  </button>
                </div>
              ))}
            </div>

          </div>
        )}
      </div>
    </div>
  )
}
